import type { Money } from './money.ts'

/**
 * ══ ĐỊNH DẠNG HIỂN THỊ — NEO CỨNG KIỂU VIỆT, MỌI NGÔN NGỮ GIAO DIỆN (C6, anh chốt 05/09) ═══════
 *
 * Không hàm nào ở đây đọc ngôn ngữ giao diện, `navigator.language`, `<html lang>` hay locale mặc
 * định của môi trường. Chứng từ kế toán Việt Nam luôn in kiểu Việt: `45.200.000` · `03/09/2026`.
 * Người dùng bật EN/中文 để đọc NHÃN, không phải để đổi định dạng sổ sách. Bản cũ cũng neo cứng
 * `vi-VN` (`proship-trans/src/lib/util.ts:42` — `fmtMoney`; `src/pages/Az.tsx:154` — `pct1`).
 * Ca kiểm `apps/web/src/dinh-dang-luon-kieu-viet.test.ts` đổi i18next sang en/zh rồi gọi lại và
 * đòi kết quả KHÔNG đổi.
 *
 * ══ ⛔ TIỀN KHÔNG BAO GIỜ ĐI QUA `Number()` — §2, và đây là LÝ DO CẢ TỆP NÀY TỰ VIẾT PHÉP LÀM TRÒN
 * Kế hoạch viết `formatVND = (m) => vnd.format(Math.trunc(Number(m)))`. Hai lỗi, cả hai đã ĐO THẬT
 * (P18 mục a):
 *   ① `Number()` — `Money` là `NUMERIC(20,2)`, kiểu cho phép 20 chữ số. Mọi giá trị vượt
 *      `Number.MAX_SAFE_INTEGER` sai ÂM THẦM: `'9007199254740993.00'` → `9.007.199.254.740.992`
 *      (thiếu 1 đồng), `'12345678901234567.00'` → `…568` (thừa 1 đồng). Không lỗi, không cảnh báo.
 *   ② `Math.trunc` — CẮT ĐUÔI thay vì NỬA LÊN: `'45200000.50'` → `45.200.000` trong khi
 *      `mulMoney` (E22, anh chốt 04/09) làm tròn nửa-lên ra `45.200.001`. Tầng tính và tầng in ra
 *      hai con số khác nhau cho cùng một khoản — lỗi xảy ra ở MỌI độ lớn, không riêng số khổng lồ.
 *
 * ⇒ Định dạng đi THẲNG TỪ CHUỖI THẬP PHÂN: tách phần nguyên/phần thập phân bằng thao tác chuỗi,
 *   làm tròn NỬA LÊN bằng phép cộng-một trên chuỗi chữ số, rồi chèn dấu chấm phân nhóm.
 *   KHÔNG `Number()`, KHÔNG `parseFloat`, KHÔNG `Intl.NumberFormat` (nó chỉ nhận `number`/`bigint`
 *   — đưa `Money` vào là đã ép qua float rồi).
 *
 * ⚠ Vì sao KHÔNG dùng `decimal.js` (mà `money.ts` đang dùng): thư viện đó là công cụ TÍNH TOÁN.
 *   Ở đây chỉ cần đọc chuỗi rồi in chuỗi — dựng một `Decimal` để `toFixed(0)` là đi vòng qua một
 *   lớp không cần thiết, và bản sao cấu hình `ROUND_HALF_UP` của `money.ts` là riêng tư của tệp đó
 *   (mở ra là nới cửa công khai của gói). Phép cộng-một dưới đây ngắn, tất định, và có ca kiểm
 *   ràng thẳng vào `mulMoney` để hai nơi không bao giờ lệch quy tắc làm tròn.
 */

/** Cùng khuôn với `money.ts` — không nhận `+`, không nhận `.5`, không nhận `5.`, không nhận `1e21`. */
const DEC_RE = /^-?\d+(\.\d+)?$/

/** '0'→'1' … '8'→'9'. '9' xử riêng (nhớ 1) nên cố ý KHÔNG có trong bảng. */
const NEXT_DIGIT: Readonly<Record<string, string>> = {
  '0': '1', '1': '2', '2': '3', '3': '4', '4': '5', '5': '6', '6': '7', '7': '8', '8': '9',
}

/** Cộng 1 vào một chuỗi CHỮ SỐ (không dấu, không dấu chấm). '999' → '1000'. Không qua `Number`. */
const addOneToDigits = (digits: string): string => {
  const a = [...digits]
  for (let i = a.length - 1; i >= 0; i--) {
    const c = a[i] as string
    if (c === '9') { a[i] = '0'; continue }
    a[i] = NEXT_DIGIT[c] as string
    return a.join('')
  }
  return `1${a.join('')}`   // tràn hết bậc: '999' → '1' + '000'
}

/**
 * `number` → chuỗi thập phân THUẦN. `String(1e21)` cho `'1e+21'` và `String(5e-7)` cho `'5e-7'` —
 * cả hai đều rơi ra ngoài `DEC_RE`, nên phải bung ký hiệu luỹ thừa ra thành chữ số thật.
 * ⚠ CHỈ dùng cho `formatQty(number)` và `formatPct1` — số lượng và phần trăm ĐÃ là `number` từ
 *   trước khi vào đây, không có gì chính xác để mất. TIỀN không bao giờ đi đường này.
 */
const numberToPlainString = (n: number): string => {
  if (!Number.isFinite(n)) return String(n)          // NaN/Infinity → rơi vào DEC_RE → ném lỗi rõ
  const s = String(n)
  const m = /^(-?)(\d+)(?:\.(\d+))?[eE]([+-]?\d+)$/.exec(s)
  if (!m) return s
  const [, sign = '', intPart = '0', fracPart = '', expStr = '0'] = m
  const exp = Number.parseInt(expStr, 10)              // SỐ MŨ, không phải tiền — an toàn
  let digits = intPart + fracPart
  let pointPos = intPart.length + exp
  if (pointPos <= 0) { digits = '0'.repeat(1 - pointPos) + digits; pointPos = 1 }
  else if (pointPos > digits.length) digits += '0'.repeat(pointPos - digits.length)
  const fracDigits = digits.slice(pointPos)
  return `${sign}${digits.slice(0, pointPos)}${fracDigits === '' ? '' : `.${fracDigits}`}`
}

const decimalString = (v: string | number): string => {
  const s = typeof v === 'number' ? numberToPlainString(v) : String(v).trim()
  if (!DEC_RE.test(s)) {
    throw new Error(
      `Giá trị "${String(v)}" không phải số hợp lệ để định dạng (cần chuỗi thập phân dạng ` +
      `45200000 hoặc -45200000.50).`,
    )
  }
  return s
}

/**
 * Bộ định dạng CHUNG: chuỗi thập phân → `45.200.000` / `1.234,56`.
 * `dpMax` = số chữ số thập phân TỐI ĐA (làm tròn nửa-lên tại đó) · `dpMin` = tối thiểu (đệm số 0).
 */
const formatDecimal = (v: string | number, dpMin: number, dpMax: number): string => {
  const s = decimalString(v)
  const negative = s.startsWith('-')
  const [intPart0 = '0', fracPart0 = ''] = (negative ? s.slice(1) : s).split('.')
  let intPart = intPart0
  let fracPart = fracPart0

  // ── LÀM TRÒN NỬA LÊN trên chuỗi chữ số (áp cho ĐỘ LỚN, dấu gắn lại ở cuối — đúng ROUND_HALF_UP
  //    của decimal.js mà `mulMoney` dùng: "equidistant ⇒ rounds away from zero").
  if (fracPart.length > dpMax) {
    const kept = fracPart.slice(0, dpMax)
    const roundUp = (fracPart.charCodeAt(dpMax) ?? 0) >= 53   // 53 = mã ký tự '5'
    fracPart = kept
    if (roundUp) {
      const bumped = addOneToDigits(intPart + kept)
      intPart = dpMax === 0 ? bumped : bumped.slice(0, bumped.length - dpMax)
      fracPart = dpMax === 0 ? '' : bumped.slice(bumped.length - dpMax)
    }
  }
  while (fracPart.length < dpMin) fracPart += '0'
  while (fracPart.length > dpMin && fracPart.endsWith('0')) fracPart = fracPart.slice(0, -1)

  intPart = intPart.replace(/^0+(?=\d)/, '')                       // '007' → '7', giữ lại '0'
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.')      // phân nhóm nghìn kiểu Việt
  const bodyText = fracPart === '' ? grouped : `${grouped},${fracPart}`
  // KHÔNG BAO GIỜ in '-0': -0,4 làm tròn về 0 chữ số là KHÔNG, không phải "âm không".
  const isZero = /^0$/.test(intPart) && /^0*$/.test(fracPart)
  return negative && !isZero ? `-${bodyText}` : bodyText
}

/**
 * Tiền VND: dấu chấm phân nhóm, KHÔNG phần thập phân (đồng VN không có hào — cùng ràng buộc với
 * `mulMoney`, E22). Nhận CHUỖI thập phân và giữ nguyên chuỗi từ đầu tới cuối.
 */
export const formatVND = (m: Money): string => formatDecimal(m, 0, 0)

/**
 * Số lượng: tối đa 2 chữ số thập phân, dấu phẩy làm dấu thập phân.
 * Nhận `string` (đi đường CHÍNH XÁC, không qua float) hoặc `number` (giá trị đã là float trước khi
 * vào đây — không có gì để mất thêm; xem `numberToPlainString`).
 */
export const formatQty = (n: string | number): string => formatDecimal(n, 0, 2)

/**
 * Phần trăm, 1 chữ số thập phân — chép `pct1()` bản cũ (`proship-trans/src/pages/Az.tsx:154`).
 * ⚠ CỐ Ý nhận `number`: phần trăm KHÔNG PHẢI TIỀN (nó là tỷ lệ tính ra từ hai khoản tiền), bản cũ
 *   cũng truyền `number`, và §2 nói về TIỀN. Đổi chữ ký thành `Money` sẽ bắt mọi chỗ gọi tự chế
 *   một phép chia chuỗi — nhiều đường tính hơn, không chính xác hơn.
 */
export const formatPct1 = (n: number): string => `${formatDecimal(n, 1, 1)}%`

/**
 * `'YYYY-MM-DD'` → `'03/09/2026'`. CỐ Ý KHÔNG dựng `Date`: chuỗi ngày thuần không mang múi giờ,
 * cho qua `Date` là mời lệch một ngày (§3). Mốc thời gian đầy đủ cũng nhận (lấy 10 ký tự đầu).
 */
export const formatDateVN = (iso: string): string => {
  const s = String(iso).slice(0, 10)
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
  if (!m) throw new Error(`Ngày "${String(iso)}" không đúng khuôn YYYY-MM-DD.`)
  return `${m[3]}/${m[2]}/${m[1]}`
}

const VN_TZ = 'Asia/Ho_Chi_Minh'

/**
 * Mốc thời gian ISO → `'03/09/2026 14:30'` theo giờ VN.
 *
 * ⚠ Dựng `Intl.DateTimeFormat` BÊN TRONG `try` — cùng lý do đã ghi ở `date.ts` (E21): dựng ở phạm
 *   vi module thì lỗi thiếu dữ liệu múi giờ ICU nổ ngay lúc import và không bao giờ chạm được
 *   nhánh `catch`, tức không bao giờ báo đúng nguyên nhân. Hai nguyên nhân hỏng KHÁC NHAU (đầu vào
 *   rác ↔ máy chủ thiếu ICU) phải ra hai câu khác nhau.
 * ⚠ `hour12: false` GIỮ NGUYÊN. Đã đo thật trên Node của dự án: nửa đêm ra `00:00`, không phải
 *   `24:00` (bẫy ICU quen thuộc, P18 mục d) — giống hệt `hourCycle: 'h23'`. Ca kiểm mốc nửa đêm
 *   canh chỗ này để không trôi theo phiên bản ICU.
 */
/**
 * Mốc thời gian ISO → NGÀY theo lịch VIỆT NAM, dạng `'03/09/2026'`.
 *
 * ⭐ DÙNG HÀM NÀY THAY CHO `formatDateVN(ts.slice(0, 10))` — sửa 18/09/2026.
 *   Máy chủ trả mốc thời gian theo UTC. Cắt 10 ký tự đầu là lấy NGÀY THEO UTC, mà giờ VN sớm hơn
 *   UTC 7 tiếng ⇒ **mọi thao tác trước 07:00 giờ VN bị lùi một ngày**. Đo thật 18/09/2026: phiếu
 *   lập lúc 01:11 sáng giờ VN hiện `17/09/2026` trên thanh tiến trình, và kỳ hoa hồng khoá lúc rạng
 *   sáng ghi "lưu trữ ngày 17/09". Một ngày lệch trên chứng từ tiền là một ngày lệch thật.
 * ⚠ CHỈ dùng cho MỐC THỜI GIAN. Chuỗi ngày thuần (`YYYY-MM-DD`) thì gọi `formatDateVN` — cho một
 *   chuỗi ngày thuần qua `Date` là mời đúng cái lệch múi giờ mà hàm này sinh ra để chặn.
 */
export const formatDateVNFromTs = (iso: string): string => formatDateTimeVN(iso).slice(0, 10)

export const formatDateTimeVN = (iso: string): string => {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) {
    throw new Error(`Mốc thời gian "${String(iso)}" không hợp lệ — không định dạng được theo giờ Việt Nam.`)
  }
  try {
    // `en-GB` cho đúng khuôn dd/mm/yyyy 24 giờ; đây là KHUÔN, không phải ngôn ngữ giao diện.
    const s = new Intl.DateTimeFormat('en-GB', {
      timeZone: VN_TZ,
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).format(d)
    return s.replace(',', '')
  } catch (e) {
    throw new Error(
      'Máy chủ thiếu dữ liệu múi giờ (ICU) cho Asia/Ho_Chi_Minh — đây là LỖI CẤU HÌNH, không được ' +
      'âm thầm in ra giờ sai.',
      { cause: e },
    )
  }
}
